import { RENEWAL, type SeasonPublic } from '@otryad/shared';

import { formatDate } from './format';
import { fmt, texts } from './texts';

/**
 * Что сказать о гарантии набора на экране оплаты (раунд 3, M2).
 *
 * - `launched` — сезон идёт: минимум оплат набран, job гарантии снят, автовозврата по ней уже не
 *   будет. Поздно платящему важно другое: сборы проходят по расписанию, ближайший — такого-то.
 * - `sale` — продажи открыты, срок известен. Окно продаж считается от открытия
 *   (`squad.saleWindowDays`) и может закончиться после первого сбора; тогда об этом сборе
 *   говорим отдельно: при недоборе возврат полный, и за него тоже (`processSaleDeadline`).
 * - `saleNoDate` — продажи без срока (старые данные).
 * - `null` — сезон закрыт, гарантии говорить не о чем.
 */
export type GuaranteeNote =
  | { kind: 'launched'; nextAt: string | null }
  | { kind: 'sale'; deadline: string; earlierGatheringAt: string | null }
  | { kind: 'saleNoDate' };

type SeasonForGuarantee = Pick<
  SeasonPublic,
  'status' | 'saleDeadline' | 'gatherings' | 'nextGatheringId'
> &
  Partial<Pick<SeasonPublic, 'renewalDiscountPct'>>;

export function guaranteeNote(se: SeasonForGuarantee): GuaranteeNote | null {
  // Ближайший предстоящий сбор считает сервер (открытый и не в прошлом)
  const next = se.gatherings.find((g) => g.id === se.nextGatheringId) ?? null;
  if (se.status === 'ACTIVE') return { kind: 'launched', nextAt: next?.startsAt ?? null };
  if (se.status !== 'SALE') return null;
  // Раунд 4, M3: у продления гарантии возврата нет — при недоборе отряд встаёт на паузу без
  // возвратов (`resolveRenewalDeadline`, ADR-25). Карточка обещала «деньги вернутся» и спорила
  // со строкой о сроке; решение о деньгах при паузе — открытый вопрос владельцу
  if (typeof se.renewalDiscountPct === 'number') return null;
  if (!se.saleDeadline) return { kind: 'saleNoDate' };
  const earlier =
    next && new Date(next.startsAt).getTime() < new Date(se.saleDeadline).getTime() ? next : null;
  return { kind: 'sale', deadline: se.saleDeadline, earlierGatheringAt: earlier?.startsAt ?? null };
}

/**
 * Что будет без оплаты (раунд 4, M3) — только то, что делает сервер:
 * - `sale` — в `saleDeadline` job `season.saleDeadline`: оплатили меньше `minToLaunch` → сезон
 *   отменяется, оплаты возвращаются, отряд распускается, состав — в набор (`processSaleDeadline`);
 * - `renewal` — сезон-продление (`renewalDiscountPct` задан): меньше `RENEWAL.pauseBelow`
 *   продлений → отряд на паузе, состав в набор (`resolveRenewalDeadline`); о деньгах не говорим —
 *   возврат при паузе продления не решён (открытый вопрос владельцу);
 * - `active` — сезон идёт: заказ на сезон открыт, пока сезон не закончен (`seasonForInvoice`),
 *   за неоплату из состава автоматически не снимают.
 * `null` — оплачено, сезон закрыт или срок неизвестен.
 */
export type UnpaidNote =
  | { kind: 'sale'; deadline: string; min: number }
  | { kind: 'renewal'; deadline: string; pauseBelow: number }
  | { kind: 'active' };

type SeasonForUnpaid = Pick<
  SeasonPublic,
  'status' | 'saleDeadline' | 'myPayment' | 'minToLaunch' | 'renewalDiscountPct'
>;

export function unpaidNote(se: SeasonForUnpaid): UnpaidNote | null {
  if (se.myPayment === 'PAID') return null;
  if (se.status === 'ACTIVE') return { kind: 'active' };
  if (se.status !== 'SALE' || !se.saleDeadline) return null;
  if (se.renewalDiscountPct !== null) {
    return { kind: 'renewal', deadline: se.saleDeadline, pauseBelow: RENEWAL.pauseBelow };
  }
  return { kind: 'sale', deadline: se.saleDeadline, min: se.minToLaunch };
}

/** Строка «что будет без оплаты» для главного и экрана оплаты. */
export function unpaidText(note: UnpaidNote, timezone: string): string {
  const t = texts.pay;
  if (note.kind === 'active') return t.unpaidActive;
  const deadline = formatDate(note.deadline, timezone);
  return note.kind === 'sale'
    ? fmt(t.unpaidSale, { deadline, min: note.min })
    : fmt(t.unpaidRenewal, { deadline, pauseBelow: note.pauseBelow });
}

/**
 * Строка у кнопок «иду / не иду» для основного участника без оплаты сезона (раунд 5, M3) —
 * только правда кода: в идущем сезоне ответ засчитывается наравне со всеми (`rsvpCounts` и
 * подтверждение сбора за 24 ч считают весь состав), за неоплату из состава автоматически не
 * снимают, оплатить можно, пока сезон идёт (`seasonForInvoice`). В продаже — срок и что будет
 * при недоборе, как на главном. `null` — оплачено или сезон закрыт.
 */
export function rsvpUnpaidText(se: SeasonForUnpaid, timezone: string): string | null {
  const note = unpaidNote(se);
  if (!note) return null;
  return note.kind === 'active' ? texts.rsvp.unpaidActive : unpaidText(note, timezone);
}
